import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Typography, Link, TextField, Button, Modal, Tooltip, Fade, Backdrop } from '@mui/material';
import { Facebook, Instagram, Twitter, LinkedIn, YouTube, Phone, Email, LocationOn } from '@mui/icons-material';
import TikTokIcon from '@mui/icons-material/MusicNote';
import AnimatedText from './TextAnimate';

const Footer = () => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm();

  const [modalInfo, setModalInfo] = useState({ open: false, message: "", success: false });

  // handle subscription fun
  const onSubmit = async (data) => {
    try {
      const res = await fetch("https://dsfbackend.vercel.app/api/subscribers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: data.email }),
      });

      const result = await res.json();

      if (res.ok) {
        setModalInfo({
          open: true,
          message: "Thank you for subscribing to Our Newsletter",
          success: true,
        });
        reset();
      } else {
        setModalInfo({
          open: true,
          message: result.error || "Failed to subscribe. Please try again.",
          success: false,
        });
      }
    } catch (error) {
      console.error("Subscription error:", error);
      setModalInfo({
        open: true,
        message: "An error occurred while subscribing. Please try again later.",
        success: false,
      });
    }
  };


  // modal closing func
  const handleClose = () => {
    setModalInfo({ ...modalInfo, open: false });
  };


  return (
    <footer>
      <Box
        sx={{
          width: '100vw%',
          backgroundColor: '#027D40',
          color: "rgba(255, 255, 255, 0.8)",
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',

        }}>

        {/* footer main section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            flexDirection: { xs: 'column', md: 'row' },
            gap: { xs: 0, md: 5 },
            justifyContent: 'center',
          }}
        >
          {/* Logo, mission, and social media icons */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: { xs: 'center', md: 'flex-start' },
              flex: { xs: 0, md: 1 },
              gap: { xs: 0, md: 1 },
              marginLeft: { xs: '20px', md: '30px' },
            }}
          >
            <a href="/" style={{ textDecoration: 'none' }}>
              <img
                src="https://i.ibb.co/D3kpDSt/Logo-White.png"
                alt="Dr. Sadiq Foundation"
                style={{
                  width: '250px',
                  height: '80px',
                  objectFit: 'cover',
                  transition: 'box-shadow 0.4s ease-in-out',
                }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 255, 255, 0.7)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'none'}
              />
            </a>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Poppins',
                fontSize: { xs: '12px', md: '16px' },
                fontWeight: '400',
                lineHeight: { xs: '18px', md: '22px' },
                textAlign: { xs: 'center', md: 'left' },
              }}
            >
              <AnimatedText text={"Our foundation cares for those in need, empowers youth and women, and supports vulnerable communities."} />
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Poppins',
                fontSize: { xs: '12px', md: '16px' },
                fontWeight: 'bold',
              }}
            >
              Social Connects
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Tooltip title="Facebook">
                <Link
                  href="https://www.facebook.com/drsadiqfoundationdsf/"
                  target="_blank"
                  sx={{
                    color: 'white',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.5)', color: ' #3b5998' }
                  }}>
                  <Facebook />
                </Link>
              </Tooltip>
              <Tooltip title="Instagram">
                <Link
                  href="https://www.instagram.com/drsadiqfoundation/"
                  target="_blank"
                  sx={{
                    color: 'white',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.5)', color: ' #E4405F' }
                  }}>
                  <Instagram />
                </Link>
              </Tooltip>
              <Tooltip title="LinkedIn">
                <Link
                  href="https://www.linkedin.com/company/drsadiqfoundation"
                  target="_blank"
                  sx={{
                    color: 'white',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.5)', color: ' #0077B5' }
                  }}>
                  <LinkedIn />
                </Link>
              </Tooltip>
              <Tooltip title="Twitter">
                <Link
                  href="https://x.com/drsadiqfdn"
                  target="_blank"
                  sx={{
                    color: 'white',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.5)', color: ' #1DB1F2' }
                  }}>
                  <Twitter />
                </Link>
              </Tooltip>
              <Tooltip title="TikTok">
                <Link
                  href="https://www.tiktok.com/@drsadiqfoundation"
                  target="_blank"
                  sx={{
                    color: 'white',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.5)', color: 'rgb(18, 19, 20)' }
                  }}>
                  <TikTokIcon />
                </Link>
              </Tooltip>
              <Tooltip title="YouTube">
                <Link
                  href="https://www.youtube.com/@DrSadiqFoundation"
                  target="_blank"
                  sx={{
                    color: 'white',
                    transition: 'transform 0.3s',
                    '&:hover': { transform: 'scale(1.5)', color: '#FF0000' }
                  }}>
                  <YouTube />
                </Link>
              </Tooltip>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                sm: "repeat(2, 1fr)",   // 2 columns on small tablets
                md: "repeat(3, 1fr)",   // 4 equal columns on medium screens and up
              },

              gap: { xs: 2, md: 4 },
            }}
          >
            {/* Quick Links */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'center', md: 'center' },
                gap: { xs: 0, md: 1 },
                mt: { xs: 1, md: 4 },
              }}
            >
              <Typography
                variant="h6"
                sx={{ fontFamily: 'Poppins', fontSize: { xs: '12px', md: '16px' }, fontWeight: 'bold', }}
              >
                Quick Links
              </Typography>
              <Link href="/projects" color="inherit" sx={{
                position: 'relative',
                fontFamily: 'Poppins',
                fontSize: { xs: '12px', md: '16px' },
                textDecoration: 'none',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '0%',
                  height: '2px',
                  bottom: 0,
                  left: 0,
                  backgroundColor: '#FF9900',
                  transition: 'width 0.3s',
                },
                '&:hover::after': {
                  width: '100%',
                },
              }}>
                Projects
              </Link>
              <Link href="/about" color="inherit" sx={{
                position: 'relative',
                fontFamily: 'Poppins',
                fontSize: { xs: '12px', md: '16px' },
                textDecoration: 'none',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '0%',
                  height: '2px',
                  bottom: 0,
                  left: 0,
                  backgroundColor: '#FF9900',
                  transition: 'width 0.3s',
                },
                '&:hover::after': {
                  width: '100%',
                },
              }}>
                About
              </Link>
              <Link href="/faq" color="inherit" sx={{
                position: 'relative',
                fontFamily: 'Poppins',
                fontSize: { xs: '12px', md: '16px' },
                textDecoration: 'none',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '0%',
                  height: '2px',
                  bottom: 0,
                  left: 0,
                  backgroundColor: '#FF9900',
                  transition: 'width 0.3s',
                },
                '&:hover::after': {
                  width: '100%',
                },
              }}>
                FAQ
              </Link>
              <Link href="/team" color="inherit" sx={{
                position: 'relative',
                fontFamily: 'Poppins',
                fontSize: { xs: '12px', md: '16px' },
                textDecoration: 'none',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '0%',
                  height: '2px',
                  bottom: 0,
                  left: 0,
                  backgroundColor: '#FF9900',
                  transition: 'width 0.3s',
                },
                '&:hover::after': {
                  width: '100%',
                },
              }}>
                Our Team
              </Link>
              <Link href="/news" color="inherit" sx={{
                position: 'relative',
                fontFamily: 'Poppins',
                fontSize: { xs: '12px', md: '16px' },
                textDecoration: 'none',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  width: '0%',
                  height: '2px',
                  bottom: 0,
                  left: 0,
                  backgroundColor: '#FF9900',
                  transition: 'width 0.3s',
                },
                '&:hover::after': {
                  width: '100%',
                },
              }}>
                News
              </Link>
            </Box>

            {/* Contact info */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'column' },
                alignItems: { xs: 'center', md: 'flex-start' },
                gap: { xs: 0, md: 1 },
                mt: { xs: 1, md: 4 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontFamily: 'Poppins',
                  fontSize: { xs: '12px', md: '16px' },
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}
              >
                Contact Info
              </Typography>

              <Box sx={{ marginTop: { xs: '0px', md: '2px' } }}>
                {/* Phone section */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    marginBottom: { xs: '5px', md: '10px' },
                  }}
                >
                  <Box>
                    <Phone sx={{
                      fontSize: 20,
                      color: "#fff"
                    }} />
                  </Box>

                  <Typography variant="body1" sx={{ fontSize: { xs: '12px', md: '16px' } }}>
                    <span>Call Us <br /></span>
                    <Link href="tel:051-111-500-505" sx={{ color: 'inherit', textDecoration: 'none' }}>
                      051-111 500 505
                    </Link>
                    <br />
                    <Link href="tel:051-4927546" sx={{ color: 'inherit', textDecoration: 'none' }}>
                      051-4927546
                    </Link>
                  </Typography>
                </Box>

                {/* Email section */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    marginBottom: { xs: '10px', md: '20px' },

                  }}
                >
                  <Box>
                    <Email sx={{
                      fontSize: 20,
                      color: "#fff"
                    }} />
                  </Box>
                  <Typography variant="body1" sx={{ fontSize: { xs: '12px', md: '16px' } }}>
                    <span>Mail Us <br /></span>
                    <Link href="mailto:info@drsadiqfoundation.org" sx={{ color: 'inherit', textDecoration: 'none' }}>
                      info@drsadiqfoundation.org
                    </Link>
                    <br />
                    <Link href="mailto:admin@drsf.org" sx={{ color: 'inherit', textDecoration: 'none' }}>
                      admin@drsf.org
                    </Link>
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'flex-start', md: 'center' },
                justifyContent: { xs: 'flex-start', md: 'center' },
                textAlign: { xs: 'left', md: 'center' },
                mt: { xs: 0, md: 4 },
              }}
            >
              {/* Location section */}
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: { xs: 'flex-start', md: 'center' },
                  gap: "5px",
                  marginBottom: { xs: '0px', md: '20px' },
                }}
              >
                <Box>
                  <LocationOn sx={{
                    fontSize: 20,
                    color: "#fff"
                  }} />
                </Box>
                <Typography variant="body1"
                >
                  <Typography variant="body2"
                    sx={{ fontFamily: 'Poppins', fontSize: { xs: '12px', md: '16px' }, fontWeight: 'bold' }}
                  >
                    <span>Visit Us <br /></span>
                  </Typography>

                  <Link
                    href="https://maps.app.goo.gl/RMzhi667iqcFU9sUA"
                    target="_blank"
                    sx={{ color: 'inherit', textDecoration: 'none', fontSize: { xs: '12px', md: '16px' } }}
                  >
                    33-C, Iran Road, <br />
                    Near Chandni Chowk, <br />
                    Satellite Town, <br />
                    Rawalpindi, Pakistan
                  </Link>
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'center' },
                  justifyContent: { xs: 'center', md: 'center' },
                  textAlign: { xs: 'center', md: 'center' },
                  width: { xs: '300px', md: '300px' },
                  gap: 1,

                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontFamily: 'Poppins',
                    fontSize: { xs: '14px', md: '18px' },
                    fontWeight: '600',
                  }}
                >
                  Subscribe to Our Newsletter
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)} style={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0px 10px 10px 0px'
                }}>
                  <TextField
                    type="email"
                    label="Email Address"
                    variant="outlined"
                    size="small"
                    sx={{ backgroundColor: 'white', borderRadius: '5px' }}
                    {...register("email", {
                      required: "Email is required",
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
                        message: "Enter a valid email address"
                      }
                    })}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                  />
                  <Button type="submit"
                    variant="contained"
                    sx={{
                      backgroundColor: "#FF9900",
                      color: "#FFFFFF",
                      fontWeight: "bold",
                      transition: "background-color 0.3s",
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        backgroundColor: "#FFAA5C",
                      },
                    }}>
                    Subscribe

                  </Button>
                </form>
                {/* success/error modal */}
                <Modal
                  closeAfterTransition
                  slots={{ backdrop: Backdrop }}
                  slotProps={{ backdrop: { timeout: 500 } }}
                  open={modalInfo.open}
                  onClose={handleClose}
                  aria-labelledby="modal-title"
                  aria-describedby="modal-description"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Fade in={modalInfo.open}>
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: { xs: "50%", sm: "400px" },
                        bgcolor: "background.paper",
                        boxShadow: 24,
                        p: 1,
                        borderRadius: "8px",
                        textAlign: "center",
                      }}
                    >
                      <Typography
                        id="modal-title"
                        variant="h6"
                        component="h2"
                        color={modalInfo.success ? "green" : "red"}
                      >
                        {modalInfo.success ? "Success!" : "Error!"}
                      </Typography>
                      <Typography
                        id="modal-description"
                        sx={{
                          mt: 2,
                        }}
                      >
                        {modalInfo.message}
                      </Typography>
                      <Button
                        onClick={handleClose}
                        sx={{
                          mt: 3,
                          backgroundColor: modalInfo.success ? "#027D40" : "red",
                          color: "#fff",
                          "&:hover": {
                            backgroundColor: modalInfo.success ? "#029D70" : "orange",
                          },
                        }}
                      >
                        Close
                      </Button>
                    </Box>
                  </Fade>
                </Modal>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Bottom line */}
      <Box
        sx={{
          textAlign: 'center',
          height: '30px',
          width: '100%',
          background: '#FFD15C',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="body2"
          sx={{ fontFamily: 'Poppins', fontSize: '12px', fontWeight: '500' }}
        >
          Dr. Sadiq Foundation&copy; 2024 All Rights Reserved.
        </Typography>
      </Box>
    </footer>
  );
};

export default Footer;
